import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../../services/api";
import SyncButton from "../../../components/SyncButton";
import SyncStatusMessage from "../../../components/SyncStatusMessage";
import SyncProgress from "../../../components/SyncProgress";
import EmptyState from "../../../components/EmptyState";
import noImage from "../../../assets/no-image.jpg";
import { getImageUrl, parseImageUrl } from "../../../utils/imageResolution";
import Steam from "@/assets/platforms/steam.svg?react";
import Xbox from "@/assets/platforms/xbox.svg?react";
import PSN from "@/assets/platforms/playstation.svg?react";

import { Game, Profile } from "../../../types";

import styles from "../Games.module.scss";

/** Visibility toggle icon — renders an eye (visible) or eye-off (hidden) SVG */
const VisibilityIcon: React.FC<{ hidden: boolean }> = ({ hidden }) => {
  if (hidden) {
    // Eye with slash (hidden state) — clicking will unhide
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        width="20"
        height="20"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  // Eye (visible state) — clicking will hide
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      width="20"
      height="20"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
};

function resolveGameCover(coverUrl: string | undefined): string {
  if (!coverUrl) return noImage;
  const parsed = parseImageUrl(coverUrl);
  if (!parsed) return coverUrl;
  return getImageUrl(parsed.imageId, "medium") ?? coverUrl;
}

interface GamesProps {
  profile: Profile;
}

const Games: React.FC<GamesProps> = ({ profile }) => {
  const { user, linkedAccounts: accounts } = profile;
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [featuredGames, setFeaturedGames] = useState<Game[]>([]);
  const [loaded, setLoaded] = useState<Record<number, boolean>>({});
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<boolean>(false);
  const [syncJobInProgress, setSyncJobInProgress] = useState<boolean>(false);
  const [showHidden, setShowHidden] = useState<boolean>(false);

  const imgRefs = useRef<HTMLImageElement[]>([]);

  // Check sync status on mount and periodically
  useEffect(() => {
    if (!profile.isMe || !accounts?.steam) return;

    const checkSyncStatus = async () => {
      try {
        const status = await api.app.getSyncStatus();
        const gamesSyncInProgress = status.jobs.some(
          (job: any) => job.jobType === "games" && job.status === "in_progress"
        );
        setSyncJobInProgress(gamesSyncInProgress);
        setSyncing(gamesSyncInProgress);
      } catch (err) {
        console.error("Error checking sync status:", err);
      }
    };

    checkSyncStatus();

    // Poll every 5 seconds if sync is in progress
    const interval = setInterval(() => {
      if (syncJobInProgress || syncing) {
        checkSyncStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [profile.isMe, accounts?.steam, syncJobInProgress, syncing]);

  useEffect(() => {
    let alive = true;

    const fetchGames = async () => {
      setLoading(true);
      try {
        const res = await api.app.getGames(user.userId);
        const games = res.games
        if (!alive) return;
        setFeaturedGames(games);

        // initialize loaded map for new list (keeps skeleton/opacity consistent)
        const nextLoaded: Record<number, boolean> = {};
        setLoaded(nextLoaded);
      } catch (err) {
        console.error("Error fetching Steam games:", err);
        if (!alive) return;
        setFeaturedGames([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    if (accounts?.steam) fetchGames();
    else {
      setFeaturedGames([]);
      setLoaded({});
      setLoading(false);
    }

    return () => {
      alive = false;
    };
  }, [accounts?.steam, user.userId]);

  // mark already-cached images as loaded
  useEffect(() => {
    imgRefs.current.forEach((img, idx) => {
      if (img?.complete && img.naturalWidth > 0) {
        setLoaded((prev) => ({ ...prev, [idx]: true }));
      }
    });
  }, [featuredGames]);

  const handleSyncGames = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncSuccess(false);
    setSyncJobInProgress(true);

    try {
      await api.app.syncUserGames();
      setSyncSuccess(true);
      
      // Refresh games list after sync
      const res = await api.app.getGames(user.userId);
      setFeaturedGames(res.games);
      
      // Reset loaded state for new games
      const nextLoaded: Record<number, boolean> = {};
      setLoaded(nextLoaded);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSyncSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error syncing games:", err);
      
      // Check if it's a 409 (sync already in progress)
      if (err.response?.status === 409) {
        setSyncError("Sync already in progress. Please wait...");
        // Keep checking status
        setSyncJobInProgress(true);
      } else {
        setSyncError(err.response?.data?.message || err.message || "Failed to sync games");
        setSyncJobInProgress(false);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleDismissMessage = () => {
    setSyncError(null);
    setSyncSuccess(false);
  };

  const handleToggleVisibility = async (
    e: React.MouseEvent,
    game: Game
  ) => {
    // Prevent the tile's navigation click
    e.stopPropagation();

    const currentlyHidden = game.status === "HIDDEN";
    const newStatus = currentlyHidden ? "ACTIVE" : "HIDDEN";

    // Optimistic update
    setFeaturedGames((prev) =>
      prev.map((g) => (g.gameId === game.gameId ? { ...g, status: newStatus } : g))
    );

    try {
      await api.app.setGameVisibility(game.gameId, !currentlyHidden);
    } catch (err) {
      console.error("Error toggling game visibility:", err);
      // Revert on failure
      setFeaturedGames((prev) =>
        prev.map((g) =>
          g.gameId === game.gameId ? { ...g, status: currentlyHidden ? "HIDDEN" : "ACTIVE" } : g
        )
      );
    }
  };

  const displayedGames = showHidden
    ? featuredGames
    : featuredGames.filter((game) => !game.status || game.status === "ACTIVE");

  return (
    <div className={styles.Games}>
      {profile.isMe && accounts?.steam && !(syncing || syncJobInProgress) && (
        <div className={styles.syncContainer}>
          <SyncButton
            loading={false}
            onClick={handleSyncGames}
            disabled={syncing || syncJobInProgress}
            text="Sync Games"
          />
          <SyncStatusMessage
            message="Games synced successfully!"
            type="success"
            visible={syncSuccess}
            onDismiss={handleDismissMessage}
          />
          <SyncStatusMessage
            message={syncError || ""}
            type="error"
            visible={!!syncError}
            onDismiss={handleDismissMessage}
          />
        </div>
      )}
      {(syncing || syncJobInProgress) && (
        <SyncProgress active label="games" />
      )}
      {profile.isMe && (
        <div className={styles.filterContainer}>
          <label className={styles.hiddenToggle}>
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
            />
            <span className={styles.toggleSlider} />
            <span className={styles.toggleLabel}>Show hidden games</span>
          </label>
        </div>
      )}
      {loading && featuredGames.length === 0 && (
        <div className={styles.loader} aria-label="Loading games">
          <div className={styles.spinner} />
        </div>
      )}
      {!loading && displayedGames.length === 0 && !(syncing || syncJobInProgress) && (
        <EmptyState heading="No games yet." />
      )}
      <div className={styles.grid}>
        {displayedGames.map((game, idx) => (
          <div
            key={`${game.name}-${idx}`}
            className={`${styles.tile}${game.status === "HIDDEN" ? ` ${styles.hiddenTile}` : ""}`}
            data-loaded={loaded[idx] ? "1" : "0"}
            tabIndex={0}
            onClick={() => navigate(`/game/${game.gameId}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/game/${game.gameId}`);
              }
            }}
            style={{ cursor: "pointer" }}
          >
            {/* Hidden badge overlay */}
            {game.status === "HIDDEN" && (
              <div className={styles.hiddenBadge}>Hidden</div>
            )}

            {/* Visibility toggle for owner */}
            {profile.isMe && (
              <button
                className={styles.visibilityToggle}
                onClick={(e) => handleToggleVisibility(e, game)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                aria-label={game.status === "HIDDEN" ? "Unhide game" : "Hide game"}
                title={game.status === "HIDDEN" ? "Unhide game" : "Hide game"}
                type="button"
              >
                <VisibilityIcon hidden={game.status === "HIDDEN"} />
              </button>
            )}

            {/* Platform badge overlay */}
            <div className={styles.titleOverlayBadge}>
              {game.sources.some(s => s.provider === "steam") && (
                <Steam className={styles.logo} />
              )}
              {game.sources.some(s => s.provider === "xbox") && (
                <Xbox className={styles.logo} />
              )}
              {game.sources.some(s => s.provider === "psn") && (
                <PSN className={styles.logo} />
              )}
            </div>

            {/* Skeleton shimmer */}
            <div className={styles.skeleton} />
            <img
              ref={(el) => {
                if (el) imgRefs.current[idx] = el;
              }}
              className={styles.cover}
              src={resolveGameCover(game.coverUrl)}
              alt={game.name}
              loading="lazy"
              onLoad={() => {
                setLoaded((prev) => ({ ...prev, [idx]: true }));
              }}
              onError={(e) => {
                const img = e.currentTarget;

                if (img.dataset.fallbackApplied === "true") {
                  setLoaded((prev) => ({ ...prev, [idx]: true }));
                  return;
                }

                img.dataset.fallbackApplied = "true";
                img.style.objectFit = "contain";
                img.src = noImage;
              }}
            />

            {/* Title overlay */}
            <div className={styles.titleOverlay}>{game.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Games;